import { getTenantScopedKey } from '../offline/tenant'

import { taskApi } from './taskApi'
import { deleteScopedKey, readWaterJSON, writeWaterJSON } from './waterStorage'
import { listStorageKeys, readItem, saveJSON, writeItem } from './storage'
import { loadProductionUnitCustomColumns, saveProductionUnitCustomColumns } from './productionUnitCustomColumns'
import { loadProductionUnitTableLegacyBlob, saveProductionUnitTableLegacyBlob } from './productionUnitTable'

type ExportedTasksPayload = {
  version: 1
  count: number
  items: any[]
}

export type SundayAppExportPayload = {
  tenantKey: string
  farms: any[]
  tableData: Record<string, any>
  customColumns: Record<string, any>
  tasks: ExportedTasksPayload
  waterData: Record<string, any>
  otherData: Record<string, any>
  timestamp: string
  browser: string
  version: string
}

const hasStorage = (): boolean => typeof window !== 'undefined' && Boolean(window.localStorage)

const clearSelectionContextKeysAfterImport = (tenantKey: string): void => {
  if (!hasStorage()) return

  const keys = Object.keys(window.localStorage)

  keys.forEach((key) => {
    const isTenantScoped = key.endsWith(`::${tenantKey}`)
    const isLegacyDefault = tenantKey === 'default' && !key.includes('::')
    if (!isTenantScoped && !isLegacyDefault) return

    if (
      key.startsWith('activeContext:')
      || key.startsWith('dashboard:')
      || key.startsWith('currentFarm::')
    ) {
      try {
        window.localStorage.removeItem(key)
      } catch {
        // ignore
      }
    }
  })
}

// findings #75: includes the IndexedDB-backed dataset keys (water tables,
// production tables), so export/import keeps covering them.
const listLocalStorageKeys = (): string[] => {
  if (!hasStorage()) return []
  return listStorageKeys()
}

const tryReadTaskItems = (raw: unknown): any[] => {
  if (typeof raw !== 'string' || raw.trim().length === 0) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed
    return parsed && Array.isArray(parsed.items) ? parsed.items : []
  } catch {
    return []
  }
}

const findTaskItemsInStorage = (tenantKey: string): any[] => {
  if (!hasStorage()) return []

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index)
    if (!key) continue

    const isTenantScoped = key.endsWith(`::${tenantKey}`)
    const isLegacyDefault = tenantKey === 'default' && (key === 'tasks:v1' || key === 'sunday.tasks.v1')
    if (!isTenantScoped && !isLegacyDefault) continue

    const raw = window.localStorage.getItem(key)
    if (!raw) continue

    let parsed: any
    try {
      parsed = JSON.parse(raw)
    } catch {
      continue
    }

    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.items)) continue
    const items = parsed.items
    if (items.length === 0) continue

    const looksLikeTasks = items.every((item: any) =>
      item && typeof item === 'object' && typeof item.id === 'string' && typeof item.title === 'string'
    )

    if (looksLikeTasks) return items
  }

  return []
}

const readLegacyFarmsFallback = (tenantKey: string): any[] => {
  if (!hasStorage()) return []
  const scopedFarmsKey = getTenantScopedKey('farms', tenantKey)

  try {
    if (tenantKey === 'default') {
      return JSON.parse(window.localStorage.getItem(scopedFarmsKey) || window.localStorage.getItem('farms') || '[]')
    }
    return JSON.parse(window.localStorage.getItem(scopedFarmsKey) || '[]')
  } catch {
    return []
  }
}

export const buildSundayAppExportPayload = async (args: {
  tenantKey: string
  farms: any[]
  tasksFromDb: any[]
}): Promise<SundayAppExportPayload> => {
  const { tenantKey, farms, tasksFromDb } = args

  let taskItems: any[] = Array.isArray(tasksFromDb) ? tasksFromDb : []

  if (taskItems.length === 0) {
    if (hasStorage()) {
      const candidates: unknown[] = [
        window.localStorage.getItem(getTenantScopedKey('tasks:v1', tenantKey)),
        // Some environments can behave oddly with special characters; keep a direct property fallback.
        (window.localStorage as any)[getTenantScopedKey('tasks:v1', tenantKey)],
        window.localStorage.getItem(getTenantScopedKey('sunday.tasks.v1', tenantKey))
      ]

      if (tenantKey === 'default') {
        candidates.push(
          window.localStorage.getItem('tasks:v1'),
          (window.localStorage as any)['tasks:v1'],
          window.localStorage.getItem('sunday.tasks.v1')
        )
      }

      for (const candidate of candidates) {
        const items = tryReadTaskItems(candidate)
        if (items.length > 0) {
          taskItems = items
          break
        }
      }

      if (taskItems.length === 0) {
        taskItems = findTaskItemsInStorage(tenantKey)
      }
    }
  }

  if (taskItems.length === 0) {
    try {
      const fetched = await taskApi.listTasks()
      if (Array.isArray(fetched) && fetched.length > 0) {
        taskItems = fetched
      }
    } catch {
      // ignore
    }
  }

  const farmsForExport = Array.isArray(farms) ? farms : readLegacyFarmsFallback(tenantKey)

  const waterKeys = new Set<string>()
  for (const farm of farmsForExport) {
    if (!farm) continue
    const farmId = typeof farm.id === 'string' ? farm.id : ''
    const farmName = typeof farm.name === 'string' ? farm.name : ''
    const productionUnits = Array.isArray(farm.productionUnits) ? farm.productionUnits : []

    for (const pu of productionUnits) {
      if (!pu) continue
      const puId = typeof pu.id === 'string' ? pu.id : ''
      const puName = typeof pu.name === 'string' ? pu.name : ''

      if (farmId && puId) {
        waterKeys.add(`water-${farmId}-${puId}`)
        waterKeys.add(`water-migrated-${farmId}-${puId}`)
        waterKeys.add(`water-v2-${farmId}-${puId}`)
        waterKeys.add(`water-migrated-v2-${farmId}-${puId}`)
      }
      if (farmName && puName) {
        waterKeys.add(`water-${farmName}-${puName}`)
        waterKeys.add(`water-${farmName} - ${puName}`)
        waterKeys.add(`water-migrated-${farmName}-${puName}`)
        waterKeys.add(`water-migrated-${farmName} - ${puName}`)
      }
    }
  }

  const waterData: Record<string, any> = {}
  const otherData: Record<string, any> = {}

  const waterKeysToExport = new Set<string>()
  waterKeys.forEach((baseKey) => {
    waterKeysToExport.add(getTenantScopedKey(baseKey, tenantKey))
    if (tenantKey === 'default') {
      waterKeysToExport.add(baseKey)
    }
  })

  for (const key of listLocalStorageKeys()) {
    try {
      if (waterKeysToExport.has(key)) {
        waterData[key] = JSON.parse(readItem(key) || '[]')
        continue
      }

      const isTenantScoped = key.endsWith(`::${tenantKey}`)
      if (isTenantScoped) {
        const value = readItem(key)
        otherData[key] = value ? JSON.parse(value) : {}
        continue
      }

      if (tenantKey === 'default') {
        if (key.startsWith('farm-') || key.includes('production-unit') || key.includes('biomass') || key.includes('feed')) {
          const value = readItem(key)
          otherData[key] = value ? JSON.parse(value) : {}
        }
      }
    } catch {
      if (waterKeysToExport.has(key)) waterData[key] = readItem(key)
      else if (key.endsWith(`::${tenantKey}`)) otherData[key] = readItem(key)
    }
  }

  return {
    tenantKey,
    farms: farmsForExport,
    tableData: loadProductionUnitTableLegacyBlob<any>(),
    customColumns: loadProductionUnitCustomColumns<any>(),
    tasks: {
      version: 1,
      count: taskItems.length,
      items: taskItems
    },
    waterData,
    otherData,
    timestamp: new Date().toISOString(),
    browser: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    version: '1.2'
  }
}

export const applyImportedStoragePayload = (args: {
  tenantKey: string
  data: any
}): void => {
  const { tenantKey, data } = args
  if (!data || typeof data !== 'object') return

  const shouldSkipImportedOtherKey = (key: string): boolean => {
    // Selection state is environment-specific and can become invalid after imports
    // (IDs may change). Importing these keys can freeze context across screens.
    if (key.startsWith('activeContext:')) return true
    if (key.startsWith('dashboard:')) return true
    if (key.startsWith('currentFarm::')) return true
    return false
  }

  if (data.tableData) {
    const validatedTableData: Record<string, any> = {}

    Object.keys(data.tableData).forEach((key) => {
      if (Array.isArray(data.tableData[key])) {
        validatedTableData[key] = data.tableData[key].map((row: any) => ({
          ...row,
          stage: ['Active', 'Inactive'].includes(row.stage) ? row.stage : 'Active',
          health: ['Good', 'Issue', 'Emergency'].includes(row.health) ? row.health : 'Good',
          lot: String(row.lot || ''),
          date: String(row.date || ''),
          weight: String(row.weight || ''),
          mort: String(row.mort || ''),
          fishStock: String(row.fishStock || ''),
          do: String(row.do || ''),
          id: String(row.id || Date.now())
        }))
      } else {
        validatedTableData[key] = data.tableData[key]
      }
    })

    saveProductionUnitTableLegacyBlob(validatedTableData)
  }

  if (data.customColumns) {
    saveProductionUnitCustomColumns(data.customColumns)
  }

  if (hasStorage() && data.waterData) {
    Object.keys(data.waterData).forEach((key) => {
      if (!key.startsWith('water-')) return
      const baseKey = key.split('::')[0]
      // Use waterStorage helper to keep tenant scoping + quota handling consistent.
      writeWaterJSON(baseKey, data.waterData[key], tenantKey)
    })
  }

  if (hasStorage() && data.otherData) {
    Object.keys(data.otherData).forEach((key) => {
      const isTenantScoped = key.endsWith(`::${tenantKey}`)
      const isLegacyDefault = tenantKey === 'default' && !key.includes('::')
      if (!isTenantScoped && !isLegacyDefault) return

      if (shouldSkipImportedOtherKey(key)) return

      const value = data.otherData[key]
      if (typeof value === 'string') {
        // saveJSON expects JSON-serializable; write raw strings directly but with quota safety.
        try {
          writeItem(key, value)
        } catch {
          // ignore
        }
      } else {
        saveJSON(key, value)
      }
    })
  }

  // Imports can change farm/unit IDs; keep context selectors from getting stuck.
  clearSelectionContextKeysAfterImport(tenantKey)
}

export const deleteLegacyWaterKey = (baseKey: string, tenantKey: string): void => {
  deleteScopedKey(baseKey, tenantKey)
}

export const readLegacyWaterJSON = <T>(baseKey: string, fallback: T, tenantKey: string): T =>
  readWaterJSON(baseKey, fallback, tenantKey)

export const writeLegacyWaterJSON = <T>(baseKey: string, value: T, tenantKey: string): void => {
  writeWaterJSON(baseKey, value, tenantKey)
}
